import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { DPC_STAGES, STATUS_META, monthLabel, stageShort } from "@/lib/dpc";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WeekShape, ApproveDialog, ProratedBadge } from "@/components/PlanBits";

const TH = "px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground";
const num = (v) => (v === "" || v == null ? 0 : Math.max(0, parseInt(v, 10) || 0));
const cellSum = (split, mo, st) => Object.values(split[mo]?.[st] || {}).reduce((a, v) => a + num(v), 0);
// keys that exist in a saved split but are not offered by the current catalog (retired sources, renamed weeks)
const extraKeys = (split, keys) => [...new Set(Object.values(split).flatMap((stages) => Object.values(stages || {}).flatMap((cell) => Object.keys(cell || {}))))].filter((k) => !keys.includes(k));
const dropKeys = (split, drop) => Object.fromEntries(Object.entries(split).map(([mo, stages]) => [mo, Object.fromEntries(Object.entries(stages || {}).map(([st, cell]) => [st, Object.fromEntries(Object.entries(cell || {}).filter(([k]) => !drop.includes(k)))]))]));

const allocate = (total, weights) => {
  let w = weights.some((x) => x > 0) ? weights : weights.map(() => 1);
  const s = w.reduce((a, b) => a + b, 0);
  const raw = w.map((x) => (total * x) / s);
  const base = raw.map(Math.floor);
  let rem = total - base.reduce((a, b) => a + b, 0);
  const order = raw.map((r, i) => [r - base[i], i]).sort((a, b) => b[0] - a[0]);
  for (let k = 0; k < rem; k++) base[order[k % order.length][1]] += 1;
  return base;
};

const workingDays = (month, start, end) => {
  const [y, m] = [+month.slice(0, 4), +month.slice(5, 7)];
  let c = 0;
  for (let d = start; d <= end; d++) if (new Date(y, m - 1, d).getDay() !== 0) c++;
  return c;
};

export default function DpcPlanEntry() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const month = params.get("month") || "";
  const [meta, setMeta] = useState(null);
  const [plan, setPlan] = useState(null);
  const [totals, setTotals] = useState({});
  const [srcSplit, setSrcSplit] = useState({});
  const [wkSplit, setWkSplit] = useState({});
  const [step, setStep] = useState(1);
  const [stage2, setStage2] = useState("ENQUIRY");
  const [stage3, setStage3] = useState("ENQUIRY");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(null); // "zonal" | "final" while the back-loading comment dialog is open

  const load = useCallback(() => {
    api.get(`/dpc/meta?${qs({ month })}`).then(({ data }) => setMeta(data)).catch(() => {});
    api.get(`/dpc/plan/${code}?${qs({ month })}`).then(({ data }) => {
      setPlan(data);
      setTotals(data.totals || {});
      setSrcSplit(data.source_split || {});
      setWkSplit(data.week_split || {});
    }).catch((e) => toast.error(apiError(e)));
  }, [code, month]);
  useEffect(() => { load(); }, [load]);

  const models = useMemo(() => (meta?.models || []).filter((m) => m.active).map((m) => m.name), [meta]);
  const sources = useMemo(() => (meta?.sources || []).filter((s) => s.active).map((s) => s.name), [meta]);
  const sourceGroups = useMemo(() => (meta?.sources || []).filter((s) => s.active && (s.members || []).length > 1), [meta]);
  const weeks = useMemo(() => meta?.weeks || [], [meta]);

  const tot = useCallback((mo, st) => num(totals[mo]?.[st]), [totals]);
  const remaining = useCallback((split, mo, st) => tot(mo, st) - cellSum(split, mo, st), [tot]);

  // Every model × stage whose source or week split does not add up — counts hidden/retired keys too, exactly like the backend.
  const mismatches = useMemo(() => {
    const models = [...new Set([...Object.keys(totals), ...Object.keys(srcSplit), ...Object.keys(wkSplit)])];
    const out = [];
    models.forEach((mo) => DPC_STAGES.forEach((st) => {
      const t = tot(mo, st), s = cellSum(srcSplit, mo, st), w = cellSum(wkSplit, mo, st);
      if (s !== t && (t > 0 || s > 0)) out.push({ mo, st, step: 2, kind: "source", have: s, want: t });
      if (w !== t && (t > 0 || w > 0)) out.push({ mo, st, step: 3, kind: "week", have: w, want: t });
    }));
    return out;
  }, [totals, srcSplit, wkSplit, tot]);
  const step2Done = !mismatches.some((m) => m.step === 2);
  const step3Done = !mismatches.some((m) => m.step === 3);
  const extraSrc = useMemo(() => extraKeys(srcSplit, sources), [srcSplit, sources]);
  const extraWk = useMemo(() => extraKeys(wkSplit, weeks.map((w) => w.week)), [wkSplit, weeks]);

  if (!plan || !meta) return <div className="text-sm text-muted-foreground py-20 text-center">Loading plan…</div>;
  const editable = plan.can_edit;
  const locked = ["submitted", "zonal_approved", "approved"].includes(plan.status);
  const sm = STATUS_META[plan.status] || STATUS_META.not_started;

  const setSplitCell = (setter, mo, st, key, val) =>
    setter((s) => ({ ...s, [mo]: { ...(s[mo] || {}), [st]: { ...((s[mo] || {})[st] || {}), [key]: val } } }));

  const applyWeights = (setter, keys, weightFn) => {
    setter((prev) => {
      const next = { ...prev };
      Object.keys(totals).forEach((mo) => DPC_STAGES.forEach((st) => {
        const t = tot(mo, st);
        if (!t) return;
        const alloc = allocate(t, weightFn(mo, st, keys));
        next[mo] = { ...(next[mo] || {}) };
        next[mo][st] = {};
        keys.forEach((k, i) => { next[mo][st][k] = alloc[i]; });
      }));
      return next;
    });
  };

  const applyEvenSources = () => applyWeights(setSrcSplit, sources, () => sources.map(() => 1));
  const applyLastMonthMix = async () => {
    const [y, m] = [+month.slice(0, 4), +month.slice(5, 7)];
    const prev = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
    try {
      const { data } = await api.get(`/dpc/plan/${code}?${qs({ month: prev })}`);
      const mix = {};
      Object.values(data.source_split || {}).forEach((stages) => Object.entries(stages || {}).forEach(([st, srcs]) => {
        mix[st] = mix[st] || {};
        Object.entries(srcs || {}).forEach(([so, q]) => { mix[st][so] = (mix[st][so] || 0) + num(q); });
      }));
      if (!Object.keys(mix).length) return toast.error(`No plan found for ${monthLabel(prev)} — using even split instead`), applyEvenSources();
      applyWeights(setSrcSplit, sources, (mo, st) => sources.map((so) => mix[st]?.[so] || 0));
      toast.success(`Applied ${monthLabel(prev)} source mix`);
    } catch { toast.error("Could not load last month's plan"); }
  };
  const applyWdWeeks = () => applyWeights(setWkSplit, weeks.map((w) => w.week), () => weeks.map((w) => workingDays(month, w.start, w.end)));

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/dpc/plan/${code}?${qs({ month })}`, { totals, source_split: srcSplit, week_split: wkSplit });
      if (!silent) toast.success(data.revision ? `Saved as plan v${data.version} — Admin & MD notified` : "Draft saved");
      (data.warnings || []).forEach((w) => toast.warning(w));
      load();
      return true;
    } catch (e) { toast.error(apiError(e)); return false; } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!(await save(true))) return;
    try {
      const { data } = await api.post(`/dpc/plan/${code}/submit?${qs({ month })}`);
      toast.success("Plan submitted for approval");
      (data.warnings || []).forEach((w) => toast.warning(w));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const action = async (path, body) => {
    try {
      const { data } = await api.post(`/dpc/plan/${code}/${path}?${qs({ month })}`, body);
      toast.success(path === "approve" ? (data?.back_loaded ? "Approved with a back-loading note" : "Approved") : "Returned to dealer");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  // A back-loaded plan needs a comment at every approval step; an even plan approves straight away.
  const approve = (step) => (plan.guard?.flagged ? setApproving(step) : action("approve"));
  const threshold = meta.settings?.week_share_threshold || 35;

  const grandTotal = (st) => [...new Set([...models, ...Object.keys(totals)])].reduce((a, mo) => a + tot(mo, st), 0);

  return (
    <div className="space-y-4" data-testid="dpc-plan-entry">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Plan — {plan.dealer_code} <span className="text-muted-foreground font-medium text-base">· {monthLabel(plan.month)}</span></h1>
          <div className="flex items-center gap-2 text-xs mt-1">
            <span className={`rounded px-1.5 py-0.5 font-semibold ${sm.cls}`} data-testid="plan-entry-status">{sm.label}</span>
            {plan.version > 1 && <span className="text-muted-foreground" data-testid="plan-entry-version">Plan v{plan.version}{plan.revised_at ? ` · revised ${plan.revised_at.slice(0, 10)}` : ""}{plan.revised_by ? ` by ${plan.revised_by}` : ""}</span>}
            {plan.status === "approved" && !plan.has_week_split && <ProratedBadge count={1} testId="plan-entry-prorated" />}
            <Link to="/dpc/plans" className="underline text-muted-foreground hover:text-foreground">← Plan board</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editable && <Button variant="outline" size="sm" onClick={() => save()} disabled={saving} data-testid="plan-save-btn">{locked ? "Save revision" : "Save draft"}</Button>}
          {plan.can_submit && <Button size="sm" onClick={submit} disabled={!step2Done || !step3Done || saving} data-testid="plan-submit-btn">Submit plan</Button>}
          {plan.can_approve_zonal && <Button size="sm" onClick={() => approve("zonal")} data-testid="plan-approve-zonal-btn">Approve (Zonal)</Button>}
          {plan.can_approve_final && <Button size="sm" onClick={() => approve("final")} data-testid="plan-approve-final-btn">Final approve (VP)</Button>}
          {plan.can_return && <Button size="sm" variant="outline" onClick={() => { const c = window.prompt("Return comment for the dealer:"); if (c) action("return", { comment: c }); }} data-testid="plan-return-btn">Return</Button>}
        </div>
      </div>

      <ApproveDialog open={!!approving} onOpenChange={(v) => !v && setApproving(null)} reasons={plan.guard?.reasons || []}
        label={approving === "final" ? "Final approve (VP)" : "Approve (Zonal)"} onConfirm={(comment) => { setApproving(null); action("approve", { comment }); }} />

      {plan.late_edit && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-xs text-amber-900" data-testid="plan-late-edit-banner">
          <b>Plan window closed on {plan.plan_window?.due}.</b> You are editing as VP Sales — {locked ? "each save creates a new plan version" : "the plan can be completed and approved here"}, and Admin &amp; MD are notified of every change.
        </div>
      )}

      {plan.comments?.length > 0 && (
        <div className="border rounded-md p-3 text-xs space-y-1 bg-card" data-testid="plan-comments">
          {plan.comments.slice(-4).map((c, i) => (
            <div key={i} className="flex flex-wrap items-baseline gap-1.5" data-testid={`plan-comment-${c.kind || "return"}`}>
              <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider ${c.kind === "approval" ? "bg-amber-100 text-amber-900" : "bg-red-100 text-red-800"}`}>{c.kind === "approval" ? (c.status === "approved" ? "approved · note" : "zonal approved · note") : "returned"}</span>
              <b>{c.by}</b> <span className="text-muted-foreground">({c.at?.slice(0, 10)})</span>: {c.comment}
            </div>
          ))}
        </div>
      )}

      <WeekShape split={wkSplit} weeks={weeks} threshold={threshold} canApprove={plan.can_approve_zonal || plan.can_approve_final} />

      <div className="flex items-center gap-1 text-xs flex-wrap">
        {[{ n: 1, l: "1 · Monthly totals", ok: true }, { n: 2, l: `2 · Source-wise split ${step2Done ? "✓" : ""}`, ok: true }, { n: 3, l: `3 · Week-wise split (W1–W4) ${step3Done ? "✓" : ""}`, ok: true }].map((s) => (
          <button key={s.n} onClick={() => setStep(s.n)} data-testid={`plan-step-${s.n}`}
            className={`px-3 py-1.5 rounded-md border font-medium transition-colors duration-200 ${step === s.n ? "bg-foreground text-background" : "hover:bg-secondary"}`}>{s.l}</button>
        ))}
        <span className="text-[11px] text-muted-foreground ml-2" data-testid="plan-steps-hint">
          Step 1 sets each model's monthly plan per stage. Steps 2 and 3 divide those same totals across lead sources and across weeks W1–W4 — fill them in any order.
        </span>
      </div>

      <Mismatches items={mismatches} jump={(m) => { setStep(m.step); (m.step === 2 ? setStage2 : setStage3)(m.st); }} />

      {step === 1 && (
        <div className="border bg-card rounded-md overflow-auto">
          <div className="px-4 py-2.5 border-b text-xs text-muted-foreground">Monthly plan per model × stage. Row and stage totals auto-sum.</div>
          <table className="w-full text-xs">
            <thead className="border-b"><tr><th className={`${TH} text-left px-3`}>Model</th>{DPC_STAGES.map((s) => <th key={s} className={`${TH} text-right`}>{stageShort(s)}</th>)}</tr></thead>
            <tbody>
              {[...models, ...Object.keys(totals).filter((mo) => !models.includes(mo) && DPC_STAGES.some((st) => tot(mo, st) > 0))].map((mo) => (
                <tr key={mo} className={`border-b last:border-0 hover:bg-muted/40 ${models.includes(mo) ? "" : "bg-amber-50/60"}`}>
                  <td className="px-3 py-1 font-medium whitespace-nowrap">{mo}{!models.includes(mo) && <span className="ml-1 text-[10px] font-normal text-amber-700" data-testid={`plan-retired-model-${mo.replace(/\s/g, "-")}`}>(retired model — clear to remove)</span>}</td>
                  {DPC_STAGES.map((st) => (
                    <td key={st} className="px-1 py-0.5 text-right">
                      <input type="number" min="0" disabled={!editable} data-testid={`plan-total-${mo.replace(/\s/g, "-")}-${st.replace(/\s/g, "-")}`}
                        className="w-20 h-7 border rounded px-1.5 text-right text-xs disabled:bg-secondary/50"
                        value={totals[mo]?.[st] ?? ""} onChange={(e) => setTotals((t) => ({ ...t, [mo]: { ...(t[mo] || {}), [st]: e.target.value === "" ? "" : num(e.target.value) } }))} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-secondary/70 font-bold border-t">
                <td className="px-3 py-1.5">ALL MODELS</td>
                {DPC_STAGES.map((st) => <td key={st} className="px-2 py-1.5 text-right num" data-testid={`plan-all-${st.replace(/\s/g, "-")}`}>{grandTotal(st)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {step === 2 && (
        <SplitGrid
          title="Split each monthly total across lead sources" keys={sources} split={srcSplit} extra={extraSrc} onClearExtra={() => setSrcSplit((s) => dropKeys(s, extraSrc))}
          keyLabels={Object.fromEntries(sourceGroups.map((g) => [g.name, g.name]))}
          keyTitles={Object.fromEntries(sourceGroups.map((g) => [g.name, `${g.name} = ${g.members.join(", ")}`]))}
          stage={stage2} setStage={setStage2} models={models} tot={tot} remaining={(mo, st) => remaining(srcSplit, mo, st)}
          flagged={new Set(mismatches.filter((m) => m.step === 2 && m.have > m.want).map((m) => m.st))}
          editable={editable} onChange={(mo, st, k, v) => setSplitCell(setSrcSplit, mo, st, k, v)}
          toolbar={editable && <>
            <Button variant="outline" size="sm" onClick={applyLastMonthMix} data-testid="plan-apply-lastmix">Apply last month's mix %</Button>
            <Button variant="outline" size="sm" onClick={applyEvenSources} data-testid="plan-apply-even">Even split</Button>
          </>}
          footer={sourceGroups.length > 0 && (
            <span data-testid="plan-source-groups-legend">
              {sourceGroups.map((g) => <span key={g.name} className="mr-3"><b>{g.name}</b> = {g.members.join(", ")} (the daily dump's individual sources roll up here)</span>)}
            </span>
          )}
          testPrefix="src"
        />
      )}

      {step === 3 && (
        <SplitGrid
          title="Split each monthly total across weeks (the week-wise plan)" keys={weeks.map((w) => w.week)} split={wkSplit} extra={extraWk} onClearExtra={() => setWkSplit((s) => dropKeys(s, extraWk))}
          keyLabels={weeks.reduce((a, w) => ({ ...a, [w.week]: `${w.week} (${w.start}–${w.end})` }), {})}
          stage={stage3} setStage={setStage3} models={models} tot={tot} remaining={(mo, st) => remaining(wkSplit, mo, st)}
          flagged={new Set(mismatches.filter((m) => m.step === 3 && m.have > m.want).map((m) => m.st))}
          editable={editable} onChange={(mo, st, k, v) => setSplitCell(setWkSplit, mo, st, k, v)}
          toolbar={editable && <Button variant="outline" size="sm" onClick={applyWdWeeks} data-testid="plan-apply-wd">Fill proportional to working days</Button>}
          testPrefix="wk"
        />
      )}

      <p className="text-[11px] text-muted-foreground">
        Submit unlocks only when the source split and week split both reconcile exactly with the monthly totals.
        {!step2Done && " · Source split pending"}{!step3Done && " · Week split pending"}
      </p>
    </div>
  );
}

function Mismatches({ items, jump }) {
  const over = items.filter((m) => m.have > m.want);
  const pending = items.length - over.length;
  if (!items.length) return null;
  const tid = (m) => `plan-mismatch-${m.kind}-${m.mo.replace(/\s/g, "-")}-${m.st.replace(/\s/g, "-")}`;
  return (
    <div className={`border rounded-md p-3 text-xs space-y-1.5 ${over.length ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50/60"}`} data-testid="plan-mismatches">
      {over.length > 0 && (
        <>
          <div className="font-semibold text-red-800" data-testid="plan-mismatch-over">{over.length} cell{over.length === 1 ? "" : "s"} allocate more than the monthly plan — lower the split or raise the total (click to jump):</div>
          <div className="flex flex-wrap gap-1.5">
            {over.slice(0, 15).map((m) => (
              <button key={tid(m)} onClick={() => jump(m)} className="rounded border border-red-300 bg-white px-2 py-0.5 hover:bg-red-100 transition-colors duration-200 num" data-testid={tid(m)}>
                {m.mo} · {stageShort(m.st)}: {m.kind} split {m.have} vs plan {m.want}
              </button>
            ))}
            {over.length > 15 && <span className="text-red-800">+{over.length - 15} more</span>}
          </div>
        </>
      )}
      {pending > 0 && <div className="text-amber-800" data-testid="plan-mismatch-pending">{pending} cell{pending === 1 ? "" : "s"} still to be split ({items.filter((m) => m.have < m.want && m.step === 2).length} source · {items.filter((m) => m.have < m.want && m.step === 3).length} week).</div>}
    </div>
  );
}

function SplitGrid({ title, keys, extra = [], onClearExtra, keyLabels = {}, keyTitles = {}, split, stage, setStage, models, tot, remaining, flagged = new Set(), editable, onChange, toolbar, footer, testPrefix }) {
  const allKeys = [...keys, ...extra];
  // models the catalog no longer offers but that still carry split quantities
  const hiddenModels = Object.keys(split).filter((mo) => !models.includes(mo) && DPC_STAGES.some((st) => cellSum(split, mo, st) > 0));
  const visibleModels = [...models, ...hiddenModels].filter((mo) => tot(mo, stage) || Object.keys(split[mo]?.[stage] || {}).length);
  const colTotal = (k) => visibleModels.reduce((a, mo) => a + (num(split[mo]?.[stage]?.[k]) || 0), 0);
  const tid = (k) => k.replace(/[^A-Z0-9]/gi, "-");
  return (
    <div className="border bg-card rounded-md overflow-auto">
      <div className="px-4 py-2.5 border-b flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">{title}</span>
        <div className="flex items-center rounded-md border overflow-hidden text-xs ml-auto">
          {DPC_STAGES.map((s) => (
            <button key={s} data-testid={`${testPrefix}-stage-${s.replace(/\s/g, "-")}`} onClick={() => setStage(s)}
              className={`relative px-2.5 py-1 font-medium transition-colors duration-200 ${stage === s ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}>
              {stageShort(s)}{flagged.has(s) && <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-red-500" data-testid={`${testPrefix}-stage-flag-${s.replace(/\s/g, "-")}`} />}
            </button>
          ))}
        </div>
        {toolbar}
      </div>
      {extra.length > 0 && (
        <div className="px-4 py-2 border-b bg-amber-50 text-[11px] text-amber-900 flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-retired-banner`}>
          <span>This plan still carries quantities under {extra.length === 1 ? "an entry" : "entries"} that are no longer offered here: <b>{extra.join(", ")}</b>. They count towards the total but were hidden until now — move them to a current column or clear them.</span>
          {editable && onClearExtra && <Button variant="outline" size="sm" className="h-6 text-[11px]" onClick={onClearExtra} data-testid={`${testPrefix}-clear-retired`}>Clear retired entries</Button>}
        </div>
      )}
      <table className="w-full text-xs">
        <thead className="border-b">
          <tr>
            <th className={`${TH} text-left px-3`}>Model</th>
            <th className={`${TH} text-right`}>Total</th>
            {keys.map((k) => <th key={k} className={`${TH} text-right ${keyTitles[k] ? "underline decoration-dotted cursor-help" : ""}`} title={keyTitles[k]}>{keyLabels[k] || k}</th>)}
            {extra.map((k) => <th key={k} className={`${TH} text-right text-amber-700`} title="Retired — no longer offered for planning">{k} <span className="normal-case font-normal">(retired)</span></th>)}
            <th className={`${TH} text-right`}>Remaining</th>
          </tr>
        </thead>
        <tbody>
          {visibleModels.map((mo) => {
            const t = tot(mo, stage);
            const rem = remaining(mo, stage);
            const hidden = !models.includes(mo);
            return (
              <tr key={mo} className={`border-b last:border-0 hover:bg-muted/40 ${hidden ? "bg-amber-50/60" : ""}`}>
                <td className="px-3 py-1 font-medium whitespace-nowrap">{mo}{hidden && <span className="ml-1 text-[10px] font-normal text-amber-700" data-testid={`${testPrefix}-retired-model-${mo.replace(/\s/g, "-")}`}>(retired model)</span>}</td>
                <td className="px-2 py-1 text-right num font-bold">{t}</td>
                {allKeys.map((k) => (
                  <td key={k} className={`px-1 py-0.5 text-right ${extra.includes(k) ? "bg-amber-50/60" : ""}`}>
                    <input type="number" min="0" disabled={!editable} data-testid={`${testPrefix}-${mo.replace(/\s/g, "-")}-${stage.replace(/\s/g, "-")}-${k.replace(/[^A-Z0-9]/gi, "-")}`}
                      className="w-16 h-7 border rounded px-1 text-right text-xs disabled:bg-secondary/50"
                      value={split[mo]?.[stage]?.[k] ?? ""} onChange={(e) => onChange(mo, stage, k, e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0))} />
                  </td>
                ))}
                <td className={`px-2 py-1 text-right num font-bold ${rem === 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`${testPrefix}-rem-${mo.replace(/\s/g, "-")}`}>{rem}</td>
              </tr>
            );
          })}
          {visibleModels.length > 0 && (
            <tr className="bg-secondary/70 font-bold border-t" data-testid={`${testPrefix}-all-row`}>
              <td className="px-3 py-1.5 whitespace-nowrap">ALL MODELS <span className="font-normal text-[10px] text-muted-foreground">· {stageShort(stage)}</span></td>
              <td className="px-2 py-1.5 text-right num" data-testid={`${testPrefix}-all-total`}>{visibleModels.reduce((a, mo) => a + tot(mo, stage), 0)}</td>
              {allKeys.map((k) => <td key={k} className={`px-2 py-1.5 text-right num ${extra.includes(k) ? "bg-amber-50/60" : ""}`} data-testid={`${testPrefix}-all-${tid(k)}`}>{colTotal(k)}</td>)}
              <td className={`px-2 py-1.5 text-right num ${visibleModels.reduce((a, mo) => a + remaining(mo, stage), 0) === 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`${testPrefix}-all-rem`}>{visibleModels.reduce((a, mo) => a + remaining(mo, stage), 0)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="px-4 py-2 text-[11px] text-muted-foreground">Every row's Remaining must be 0 (a negative number means the split allocates more than the monthly total). Models with no plan for this stage are hidden.{footer && <> · {footer}</>}</p>
    </div>
  );
}
